import pRetry from 'p-retry';
import { AdditionalInfoFormViewNext } from '../../../types';
import { boloAdditionalInfoToView } from '../../../helpers/bolo-additional-info-to-view';
import { useAdditionalInfosBoloResource } from '../use-additional-infos-bolo-resource';
import { createStreamListener } from './helpers';
import { AdditionalInfosController } from './types';

export interface CreateBoloControllerConfig {
    entityId: number;
    entityNumber: string;
    resource: ReturnType<typeof useAdditionalInfosBoloResource>;
    pageSize?: number;
    /** When false, the BOLO stream listener skips reconciling the pinned section.
     *  Opt-in, mirroring the event controller: the BOLO panel passes true; any
     *  caller that must never show the pinned section leaves it off so a pin/unpin
     *  stream event can't back-door a pinned entry into the store. */
    enablePinnedEntries?: boolean;
}

/**
 * BOLO controller.
 *
 * BOLOs use entirely different API endpoints (/bolo/additional_info) and a
 * different WebSocket stream ('boloAdditionalInfo'). The `boloAdditionalInfo`
 * listener handles incremental upsert/delete by boloLinkId. The `infos`
 * listener triggers a full page reload on any message — BOLOs don't do
 * selective insert/evict like events.
 */
export function createBoloController(
    config: CreateBoloControllerConfig
): AdditionalInfosController {
    const { entityId, entityNumber, resource, pageSize, enablePinnedEntries = false } = config;

    return {
        async loadPage({ cursor }) {
            return pRetry(
                async () => {
                    if (!entityId) {
                        return {
                            additionalInfos: [],
                            pagination: {},
                            agencyEventNumberToIdMap: {},
                        };
                    }

                    const response = await resource.getInfosByCursor({
                        boloId: entityId,
                        limit: pageSize,
                        cursor,
                    });
                    const data = response?.data?.data;
                    return {
                        additionalInfos:
                            data?.boloAdditionalInfos.map((info) =>
                                boloAdditionalInfoToView(info, entityNumber)
                            ) ?? [],
                        pagination: {},
                        agencyEventNumberToIdMap: {},
                    };
                },
                { retries: 2 }
            );
        },

        async createInfo(info: AdditionalInfoFormViewNext) {
            const result = await resource.createAdditionalInfo({
                boloAdditionalInfo: {
                    text: info.text,
                    additionalInfoPriority: info.isHighPriority
                        ? 'HIGH_PRIORITY_UPDATE'
                        : 'STANDARD_UPDATE',
                    isPinned: false,
                },
                boloId: entityId,
            });
            const newView = result?.data?.data?.boloAdditionalInfoView?.boloAdditionalInfo;
            return newView ? [boloAdditionalInfoToView(newView, entityNumber)] : [];
        },

        async deleteInfo(infoId: number) {
            await resource.deleteInfo(infoId);
        },

        async editInfo({ additionalInfoId, text }) {
            const result = await resource.editInfo({
                boloLinkId: additionalInfoId,
                text,
            });
            const responseData = result.data.data;
            const boloAdditionalInfo = responseData?.boloAdditionalInfoView?.boloAdditionalInfo;
            if (!responseData || !boloAdditionalInfo) {
                throw new Error(
                    `Edit BOLO narrative returned no data for boloLinkId=${additionalInfoId}`
                );
            }
            const view = boloAdditionalInfoToView(boloAdditionalInfo, entityNumber);
            return { id: responseData.boloLinkId, text: view.text ?? '', priority: view.priority };
        },

        async setPriority({ additionalInfoId, priority }) {
            const result = await resource.setPriority({
                boloLinkId: additionalInfoId,
                priority,
            });
            const responseData = result.data.data;
            const boloAdditionalInfo = responseData?.boloAdditionalInfoView?.boloAdditionalInfo;
            if (!responseData || !boloAdditionalInfo) {
                throw new Error(
                    `Emphasize BOLO narrative returned no data for boloLinkId=${additionalInfoId}`
                );
            }
            const view = boloAdditionalInfoToView(boloAdditionalInfo, entityNumber);
            return { id: responseData.boloLinkId, text: view.text ?? '', priority: view.priority };
        },

        async getPinnedNarratives() {
            const response = await resource.getPinnedNarratives({ boloId: entityId });
            const pinned = response?.data?.data?.pinnedBoloAdditionalInfos ?? [];
            return pinned.map((info) => boloAdditionalInfoToView(info, entityNumber));
        },

        async pinNarrative(infoId: number) {
            const response = await resource.pinNarrative({ boloLinkId: infoId });
            const boloAdditionalInfo =
                response?.data?.data?.boloAdditionalInfoView?.boloAdditionalInfo;
            if (!boloAdditionalInfo) {
                throw new Error(`Pin BOLO narrative returned no data for boloLinkId=${infoId}`);
            }
            return boloAdditionalInfoToView(boloAdditionalInfo, entityNumber);
        },

        async unpinNarrative(infoId: number) {
            const response = await resource.unpinNarrative({ boloLinkId: infoId });
            const boloAdditionalInfo =
                response?.data?.data?.boloAdditionalInfoView?.boloAdditionalInfo;
            if (!boloAdditionalInfo) {
                throw new Error(`Unpin BOLO narrative returned no data for boloLinkId=${infoId}`);
            }
            return boloAdditionalInfoToView(boloAdditionalInfo, entityNumber);
        },

        createStreamListeners({ store, fetchNextPage, reloadPinnedNarratives }) {
            const boloAdditionalInfo = createStreamListener({
                streamNames: ['boloAdditionalInfo'],
                onMessage: (message) => {
                    const { boloId, boloAdditionalInfo, boloLinkId } = message.payload;
                    if (boloId !== entityId) {
                        return;
                    }

                    const isDelete = !boloAdditionalInfo;
                    if (isDelete) {
                        // removeInfos also prunes the pinned section, so a deleted
                        // narrative can't linger there.
                        store.removeInfos([boloLinkId]);
                        return;
                    }

                    const view = boloAdditionalInfoToView(boloAdditionalInfo, entityNumber);
                    store.upsertInfos([view]);

                    // Reconcile the separate pinned section from the same BOLO stream
                    // event. Pin state is per-BOLO (it lives on the BOLO link row); the
                    // boloId === entityId guard above already scopes this to the BOLO in
                    // view, so a pin can't leak across entities. addPinnedEntry drops any
                    // existing copy by id first, so a repeated add is idempotent. Gated by
                    // enablePinnedEntries for symmetry with the event controller.
                    if (enablePinnedEntries) {
                        if (view.isPinned) {
                            store.addPinnedEntry(view);
                        } else {
                            store.removePinnedEntry(view.id);
                        }
                    }
                },
            });

            const infos = createStreamListener({
                streamNames: ['infos'],
                onMessage: () => {
                    if (entityId) {
                        fetchNextPage({ reset: true });
                        if (enablePinnedEntries) {
                            reloadPinnedNarratives();
                        }
                    }
                },
            });

            return { infos, boloAdditionalInfo };
        },

        createFilterDirective() {
            return () => true;
        },
    };
}
